import { Box, Field, Flex, StackProps, useDisclosure } from "@biblioteksentralen/react";
import { unique } from "radash";
import { HoldingText } from "../HoldingText";
import { ManifestationStatusForUser } from "../manifestations/ManifestationStatusForUser";
import { FindInLibraryModal } from "../works/FindInLibraryModal";
import { ReservationButton } from "./ReservationButton";
import { ReservationContainer, reservationFullWidthBreakpoint } from "./ReservationContainer";
import { ReservationLanguageMenu } from "./ReservationLanguageMenu";
import { ReservationVariantButtons } from "./ReservationVariantButtons";
import { formatLanguagesList } from "@libry-content/integrations";
import { useWorkViewContext } from "../works/WorkViewContextProvider";
import FindInLibraryButton from "../works/FindInLibraryButton";

/**
 * For movies the order of choice is oppsite of the usual: format -> language.
 * Therefore the logic is different than for other reservations and we use a separate component.
 */
export const MovieReservation = (stackProps: StackProps) => {
  const {
    open: findInLibraryModalOpen,
    onOpen: openFindInLibraryModal,
    onClose: closeFindInLibraryModal,
  } = useDisclosure();

  const { work, holdingsData, currentVariant, manifestationStatusForUser, libraries } = useWorkViewContext();
  const representativeWorkTriple = work?.representativeWorkTriple;

  if (!work || !representativeWorkTriple) return null;

  const variants = work.getVariantsWithTriples();

  // Put language and document type in the same button(s) if there is less than four options total
  // and if the langauge labels are less than 10 characters
  const languageAndDocumentTypeInSameButton =
    variants.length < 4 &&
    variants.every(({ variant }) => (formatLanguagesList(variant.languages ?? [])?.length ?? 0) < 10);

  const currentAvailableLanguages = currentVariant?.variant.signatures.documentTypeSignature
    ? work.getLanguagesWithDocumentTypeLabel(currentVariant.variant.signatures.documentTypeSignature)
    : [];

  const variantsUniqueInDocumentType = unique(
    variants.filter(({ variant }) => !!variant.signatures.documentTypeSignature),
    ({ variant }) => variant.signatures.documentTypeSignature!
  ).map(({ variant }) => variant);

  return (
    <ReservationContainer documentType={currentVariant?.variant.documentType} {...stackProps}>
      <Box width="100%" position="relative">
        <Field.Root aria-label="form">
          {variantsUniqueInDocumentType && variantsUniqueInDocumentType.length > 0 && (
            <ReservationVariantButtons
              useLanguageMenu={languageAndDocumentTypeInSameButton}
              variants={variantsUniqueInDocumentType}
              marginBottom={{ base: "0.75rem", [reservationFullWidthBreakpoint]: "1rem" }}
            />
          )}

          {!languageAndDocumentTypeInSameButton && currentAvailableLanguages.length > 1 && (
            <Box marginBottom="0.75rem">
              <ReservationLanguageMenu languages={currentAvailableLanguages} />
            </Box>
          )}
        </Field.Root>
        <HoldingText
          work={work}
          workTriples={currentVariant?.workTriples}
          holdings={holdingsData?.holdings}
          manifestationStatusForUser={manifestationStatusForUser}
          marginBottom="0.5rem"
          justifyContent="center"
          alignItems="center"
          textProps={{ fontWeight: "normal", fontSize: "md" }}
          separateLines
        />
        <ManifestationStatusForUser
          manifestationStatusForUser={manifestationStatusForUser}
          documentType={currentVariant?.variant.documentType}
          marginBottom="0.5rem"
        />
        {manifestationStatusForUser && (
          <ReservationButton
            representativeWorkTriple={representativeWorkTriple}
            variantWithTriples={currentVariant}
            manifestationStatusForUser={manifestationStatusForUser}
            height="3rem"
          />
        )}
        <Flex justifyContent="center" marginTop="0.75rem">
          <FindInLibraryButton
            variant="secondary"
            width="100%"
            height="3rem"
            fontSize="lg"
            openFindInLibraryModal={openFindInLibraryModal}
          />
        </Flex>
      </Box>

      <FindInLibraryModal
        work={work}
        isOpen={findInLibraryModalOpen}
        onClose={closeFindInLibraryModal}
        representativeWorkTriple={representativeWorkTriple}
        workTriples={currentVariant?.workTriples}
        libraries={libraries}
        isRepresentedWork
      />
    </ReservationContainer>
  );
};
